'use client';

// Widgets that load once the page is ready (components/LateWidgets.tsx:
// dialogs, the rating card, the cookie banner, back-to-top, the compare
// tray). They used to be parsed and hydrated with the page on every visit,
// in the busiest second of it (speed plan, point 1, 2026-09-24).
//
// A message for one of them sent before it has loaded - a notice, a confirm,
// a rating request - is kept and delivered when it mounts, and asks for the
// widgets to load at once, so nothing is lost by the wait.

import { useEffect, useState } from 'react';

/** Sent when something needs a late widget now. */
export const WIDGET_WANTED_EVENT = 'rec:widget-wanted';

const queued = new Map<string, unknown[]>();
const listening = new Set<string>();

/** Deliver an event to a late widget, now or as soon as it listens. */
export function sendToWidget(type: string, detail: unknown) {
  if (typeof window === 'undefined') return;
  if (listening.has(type)) {
    window.dispatchEvent(new CustomEvent(type, { detail }));
    return;
  }
  const list = queued.get(type) ?? [];
  list.push(detail);
  queued.set(type, list);
  window.dispatchEvent(new Event(WIDGET_WANTED_EVENT));
}

/** A late widget is listening for this event: deliver what waited. Returns the undo. */
export function widgetListening(type: string): () => void {
  listening.add(type);
  const waiting = queued.get(type) ?? [];
  queued.delete(type);
  for (const detail of waiting) window.dispatchEvent(new CustomEvent(type, { detail }));
  return () => listening.delete(type);
}

/**
 * True once the page has loaded and the browser has a quiet moment - or at
 * the latest `timeout` ms after load - or as soon as a late widget is wanted.
 */
export function usePageReady(timeout = 2500): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (ready) return;
    let idle: number | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const go = () => setReady(true);
    const schedule = () => {
      if ('requestIdleCallback' in window) idle = window.requestIdleCallback(go, { timeout });
      else timer = setTimeout(go, 1000);
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
    window.addEventListener(WIDGET_WANTED_EVENT, go);
    return () => {
      window.removeEventListener('load', schedule);
      window.removeEventListener(WIDGET_WANTED_EVENT, go);
      if (idle !== undefined) window.cancelIdleCallback(idle);
      clearTimeout(timer);
    };
  }, [ready, timeout]);
  return ready;
}
